import React, { useState } from "react";
import { kitchenService } from "../../services";import { toast } from 'react-toastify';
interface Form4Props {
  onNext: (data: Form4Data) => void;
  initialData?: Form4Data;
}

export interface Form4Data {
  operatingDays: string[];
  openTime: string;
  closeTime: string;
  avgPreparationTime: number;
  acceptsPreOrders: boolean;
  maxOrdersPerHour: number;
}

const Form4: React.FC<Form4Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form4Data>({
    operatingDays: initialData?.operatingDays || [],
    openTime: initialData?.openTime || "",
    closeTime: initialData?.closeTime || "",
    avgPreparationTime: initialData?.avgPreparationTime || 30,
    acceptsPreOrders: initialData?.acceptsPreOrders || false,
    maxOrdersPerHour: initialData?.maxOrdersPerHour || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const daysOfWeek = [
    { label: "Mon", value: "MON" },
    { label: "Tue", value: "TUE" },
    { label: "Wed", value: "WED" },
    { label: "Thu", value: "THU" },
    { label: "Fri", value: "FRI" },
    { label: "Sat", value: "SAT" },
    { label: "Sun", value: "SUN" },
  ];

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      operatingDays: prev.operatingDays.includes(day)
        ? prev.operatingDays.filter((d) => d !== day)
        : [...prev.operatingDays, day],
    }));
    setError("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));
    setError("");
  };

  const handleToggle = () => {
    setFormData((prev) => ({
      ...prev,
      acceptsPreOrders: !prev.acceptsPreOrders,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (formData.operatingDays.length === 0) {
      setError("Please select at least one operating day");
      return;
    }
    if (!formData.openTime) {
      setError("Opening time is required");
      return;
    }
    if (!formData.closeTime) {
      setError("Closing time is required");
      return;
    }
    if (formData.openTime >= formData.closeTime) {
      setError("Closing time must be after opening time");
      return;
    }
    if (!formData.avgPreparationTime || formData.avgPreparationTime <= 0) {
      setError("Average preparation time must be greater than 0");
      return;
    }
    if (!formData.maxOrdersPerHour || formData.maxOrdersPerHour <= 0) {
      setError("Max orders per hour must be greater than 0");
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep4(formData);
      toast.success(response.message || 'Operational details saved successfully!');
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to save operational details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Operational Details
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Operating Days */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-Urbanist font-medium text-gray-700">
              Operating Days
            </label>
            <div className="flex gap-2 flex-wrap">
              {daysOfWeek.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  className={`px-4 py-2 text-sm font-Urbanist font-semibold rounded-lg transition-all ${
                    formData.operatingDays.includes(day.value)
                      ? "bg-gradient-to-r from-orange-500 to-orange-400 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Operational Hours */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-Urbanist font-medium text-gray-700">
              Operational Hours
            </label>
            <div className="grid grid-cols-2 gap-4">
              {/* Opening Time */}
              <div className="relative">
                <input
                  type="time"
                  id="openTime"
                  name="openTime"
                  value={formData.openTime}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer "
                  required
                />
              </div>

              {/* Closing Time */}
              <div className="relative">
                <input
                  type="time"
                  id="closeTime"
                  name="closeTime"
                  value={formData.closeTime}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
                  required
                />
              </div>
            </div>
          </div>

          {/* Avg Preparation Time */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="avgPreparationTime"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Avg Preparation Time
            </label>
            <input
              type="number"
              id="avgPreparationTime"
              name="avgPreparationTime"
              value={formData.avgPreparationTime || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="30 minutes"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              min="1"
            />
          </div>

          {/* Accept Pre-orders */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-gray-700">
              Accept Pre-orders?
            </label>
            <button
              type="button"
              onClick={handleToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.acceptsPreOrders ? "bg-orange-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.acceptsPreOrders ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Max Orders per Hour */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="maxOrdersPerHour"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Max Orders per Hour
            </label>
            <input
              type="number"
              id="maxOrdersPerHour"
              name="maxOrdersPerHour"
              value={formData.maxOrdersPerHour || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter maximum order that you can take"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              min="1"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? "Saving..." : "Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form4;
